// 레이드 2단계 넷코드(docs/RAID-NET.md, RAID-PRD 0절 2단계, CONTENT-90D-PRD 6.2): 서버 권위 보스 + 클라 권위 플레이어.
// - 서버(src/server/raid.ts RaidRoom)가 이 파일의 순수 시뮬(raidStep)을 60Hz 정수 틱으로 돌린다(DO 알람 10Hz × 6틱).
//   보스 상태기계(페이즈·패턴 타임라인·HP·비행·광폭·경직)·빈자리 AI 용병·불 장판·위협이 서버에만 있다.
// - 각 클라는 자기 영웅 피격을 로컬로 판정한다(서버가 보낸 경고 = 판정 모양·판정 틱 그대로 — 보이는 대로 맞는다).
//   서버는 용병만 판정하고, 사람은 서버 위치로 "대략 체력"(2배)만 세어 쓰러짐 숨기기를 막는다.
// - 타격: 클라가 피해를 보내면 서버가 빈도 통·레벨 피해 통·거리·무적으로 자르고 HP를 깎는다. 보상은 서버 RNG(비밀 소금) → D1 우편함.
// - 상태(RaidWorld)는 평범한 JSON 객체다: DO가 몇 초마다 저장소에 써 두고 인스턴스가 다시 떠도 같은 판을 잇는다.
// 순수 데이터·함수만 둔다(DOM·Workers API 없음). 수치는 1단계(game/raid.ts·content/raid.ts)와 같다.
import { DAMAGE_CAP, FIXED_DT, deg } from './config';
import { HEAL, MERCS, MERC_REVIVE, TAUNT, WYVERN, WyvernPattern, levelBase, mercStats, pickTarget, raidHardDef, raidTuning, type RaidDiff, type RaidId, type RaidTuning } from './content/raid';
import { TeleShape, type TeleShapeId } from './types';
import type { HeroId } from './content/types';
import { hashSeed, nextRand, rollGear, rolledItem, seedRng, type GearItem } from './gear';
import { EQUIPS, RAIDS } from './content/items';
import { stoneTierForIlvl, type StoneTier } from './content/economy';

/** 프로토콜 버전(메시지 모양이 바뀌면 올린다) */
/** 2 = 레이드 하드(C13): 'go'의 diff, 경고의 주인 칸 o(잿불 낙인이 따라다닌다) */
export const RAID_PROTO_VERSION = 2;
export const RAID_WS_PATH = '/api/raid/ws';
/** 레이드 방 정원(실 + 용병 합계) */
export const RAID_CAP = 4;
/** 서버 알람 간격·알람당 시뮬 틱(60Hz) */
export const RAID_TICK_MS = 100;
export const RAID_TICKS_PER_ALARM = 6;
/** 로비 대기: 첫 사람이 들어오고 이만큼 지나면(모두 준비) 빈자리를 용병으로 채우고 출발(PRD 6.1 "대기 30초 초과 시 AI 용병 자동 충원") */
export const RAID_FILL_MS = 30_000;
export const RAID_COUNTDOWN_MS = 3000;
/** 판 시작 장벽 상한: 카운트다운이 끝나고 이만큼 지나면 덜 읽은 사람이 있어도 시작한다(느린 기기가 판을 붙잡지 않게) */
export const RAID_LOAD_MAX_MS = 30_000;
/** 판 제한(광폭 6분 + 여유). 넘으면 실패 */
export const RAID_RUN_MAX_MS = 9 * 60_000;
/** 끊긴 사람 자리 유지(재접속 복원) */
export const RAID_GRACE_MS = 90_000;
export const RAID_IDLE_CLOSE_MS = 10 * 60_000;
/** 사람 쓰러짐 → 서버가 일으키기(용병과 같은 15초·50%) */
export const RAID_REVIVE_TICKS = MERC_REVIVE.ticks;
/** 클라 메시지 최대 바이트 */
export const RAID_MAX_IN_BYTES = 512;
/** 판 상태 저장 간격(DO 재시작 복원) */
export const RAID_PERSIST_MS = 2000;
/** 이동 검사(파티와 같은 거리 통) */
export const RAID_MOVE = { maxSpeed: 12, burst: 10, epsilon: 0.25 } as const;
/** 타격 검사: 빈도 통·보스와의 거리(서버 위치, 원거리 18m + 보스 반경 + 지연 여유)·거부 누적 추방 */
export const RAID_HIT = { hitsPerSec: 80, burst: 160, range: 24, rejectKick: 40 } as const;
export const RAID_MSG = { perSec: 4, burst: 12 } as const;
/** 주 보상 횟수(레이드별 — items.ts RAIDS weeklyLimit과 같다) */
export const RAID_WEEKLY = 3;
/** 레이드 해금(마을② = 지역1 10스테이지)을 서버 진행으로 확인: 깬 노말 스테이지 수가 이만큼 아래면 보상 없음(RAIDS unlock town 2) */
export const RAID_UNLOCK_STAGES = 10;
/** 보상 자격: 사람 피해 합 중 이 비율 이상(구경만 한 사람 배제) */
export const RAID_ELIGIBLE_RATIO = 0.05;

/** 무대: 레이드 지도 RAID_MAPS.raid_wyvern의 섬(disk(0, −46, 19)). 서버는 원으로만 판정한다(안에 막는 것이 없다 — stageMaps 주석) */
export const RAID_ARENA = {
  cx: 0, cz: -46, r: 19, pad: 1.2,
  /** 사람·용병 시작 자리(섬 남쪽, 칸 순서) */
  spawns: [{ x: -3, z: -31 }, { x: 3, z: -31 }, { x: -1, z: -33 }, { x: 1, z: -33 }] as const,
  bossAt: { x: 0, z: -54 },
} as const;

/** 사람 수별 보스 HP 배율: 사람 1 + 용병 3 = 1(1단계 수치). 용병 한 명 몫 ≈ 영웅의 13%(content/raid MERCS 주석) */
export const RAID_HP_MULT: readonly number[] = [1, 1, 1.4, 1.9, 2.4];
/** 빈자리를 채우는 용병 순서(MERCS 번호): 방패 → 사제 → 궁수 */
export const MERC_FILL: readonly number[] = [0, 2, 1];

/** 서버가 세는 사람 대략 체력(레벨 기본 HP × 2 — 방어·회피·치유 몫을 넉넉히, partyServerHp와 같은 생각) */
export function raidServerHp(level: number): number { return Math.round(levelBase(level).maxHp * 2); }
/** 서버 저장 진행(깬 스테이지 수)으로 인정하는 최대 레벨(파티와 같은 식) */
export function raidLevelByStages(stages: number): number { return Math.max(1, Math.min(99, Math.round(10 + 2.5 * Math.max(0, stages)))); }
/** 레벨 기반 초당 피해 상한(레이드 보스 한 마리 — 파티 몹몰이 통의 2배: 권장 Lv25 기대 DPS의 약 3배). 버스트 = 3초분 */
export function raidDpsCap(level: number): number {
  const lv = Math.max(1, Math.min(99, Math.floor(level) || 1));
  return 1200 + 520 * lv;
}

// ── 시뮬 상태(JSON 그대로 저장) ─────────────────────────

export const RaidSlotKind = { None: 0, Human: 1, Merc: 2 } as const;
/** 칸 동작(렌더): 서기·걷기·공격·시전·쓰러짐 */
export const RaidAct = { Idle: 0, Move: 1, Attack: 2, Cast: 3, Down: 4 } as const;
/** 보스 상태(렌더·HUD — shared/types BossState와 같은 번호) */
export const RBoss = { Intro: 1, Chase: 3, SlamWindup: 4, Slam: 5, SweepWindup: 6, Sweep: 7, Stagger: 9, Dying: 10, Dead: 11, Cast: 12 } as const;

export interface RaidSlot {
  kind: number;
  /** 용병이면 MERCS 번호(-1 = 사람·빈칸) */
  merc: number;
  x: number; z: number; f: number;
  hp: number; maxHp: number; atk: number;
  /** 쓰러짐: 다시 일어나기까지 틱(0 = 서 있음) */
  down: number;
  act: number; actTick: number;
  threat: number; dmg: number;
  /** 용병 두뇌 */
  hitClock: number; skillClock: number; dodgeAt: number; evading: boolean; evX: number; evZ: number;
  /** 판에서 빠진 사람(나감·유예 지남): 다시 일어나지 않고 표적·생존 판정에서 빠진다(피해 기록은 정산에 남긴다) */
  gone?: boolean;
}
export interface RaidTele {
  /** 경고 번호(판마다 1부터, 클라가 같은 경고를 두 번 그리지 않게) */
  n: number;
  pattern: number;
  shape: TeleShapeId;
  x: number; z: number; r: number; a: number; w: number;
  born: number; due: number;
  mult: number; heavy: boolean;
  /** 이 경고에 이미 맞은 칸(비트) */
  mask: number;
  /** 잿불 낙인(하드)의 주인 칸(-1·없음 = 일반 경고). 원이 그 칸을 따라다니고, 주인은 늘 맞는다(bearerDmg), 원 안의 다른 칸은 crowdDmg */
  o?: number;
}
export interface RaidFire { x: number; z: number; r: number; ttl: number; clock: number }
export interface RaidBoss {
  x: number; z: number; f: number; hp: number; maxHp: number;
  state: number; stateTick: number;
  /** 비행 높이 비율 0..1 */
  air: number; phase: number; enraged: boolean; enrageIn: number;
  stagger: number; staggerAcc: number;
}
export interface RaidWorld {
  raid: RaidId;
  /** 난이도(C13). 저장된 옛 판(없음)은 노말 */
  diff?: RaidDiff;
  tick: number;
  rng: number;
  boss: RaidBoss;
  slots: RaidSlot[];
  teles: RaidTele[];
  fires: RaidFire[];
  teleSeq: number;
  /** 두뇌 */
  target: number; tauntBy: number; tauntTicks: number; retarget: number; cooldown: number; attacks: number; lastPattern: number;
  /** 지금 지상 패턴(-1 없음), 돌진 남은 거리·방향 */
  pattern: number; dashLeft: number; dashAngle: number;
  /** 비행: 0 지상, 1 이륙, 2 브레스, 3 착지 경고 */
  flight: number; flightClock: number; groundLeft: number; breathMade: number; landX: number; landZ: number;
  /** 끝: 0 진행, 1 처치, 2 전멸 */
  over: number;
  /** 보낼 사건(서버가 매 알람 비운다) */
  out: RaidEvent[];
}

/** 사건(방송): 서버 → 클라 메시지로 그대로 옮긴다 */
export type RaidEvent =
  | { e: 'pat'; id: number }
  | { e: 'tel'; t: RaidTele }
  | { e: 'boom'; n: number }
  | { e: 'fire'; x: number; z: number; r: number; ttl: number }
  /** 칸 피해(서버 판정: 용병 또는 사람 대략 체력) — d 피해, hp 남은 체력 */
  | { e: 'hurt'; s: number; d: number; hp: number }
  /** 칸 사건: 공격(a = 준 피해)·치유(a = 받는 칸, c = 양)·도발(a = 틱)·쓰러짐·일어남·회피 */
  | { e: 'mev'; s: number; k: number; a: number; c: number }
  | { e: 'boss'; k: 'stagger' | 'dying' | 'dead' };
export const MevKind = { Attack: 0, Heal: 1, Taunt: 2, Down: 3, Revive: 4, Dodge: 6 } as const;

export interface RaidSlotSpec { kind: number; merc?: number; level: number }

function rngNext(w: RaidWorld): number {
  let t = (w.rng = (w.rng + 0x6d2b79f5) >>> 0);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}
const rngRange = (w: RaidWorld, a: number, b: number): number => a + (b - a) * rngNext(w);
const rngInt = (w: RaidWorld, a: number, b: number): number => a + Math.floor(rngNext(w) * (b - a));

function emptySlot(): RaidSlot {
  return { kind: RaidSlotKind.None, merc: -1, x: 0, z: 0, f: Math.PI, hp: 0, maxHp: 1, atk: 1, down: 0, act: RaidAct.Idle, actTick: 0, threat: 0, dmg: 0, hitClock: 0, skillClock: 0, dodgeAt: -1, evading: false, evX: 0, evZ: 0 };
}

/**
 * 새 판. specs = 칸 4개(사람·용병·빈칸). 사람 HP는 서버 대략 체력, 용병은 1단계와 같은 레벨 수치(레벨 = 사람 평균 — 호출자가 정한다).
 * humans = 사람 수(보스 HP 배율)
 */
export function newRaidWorld(raid: RaidId, seed: number, specs: readonly RaidSlotSpec[], diff: RaidDiff = 'normal'): RaidWorld {
  const humans = specs.filter((s) => s.kind === RaidSlotKind.Human).length;
  const d: RaidDiff = diff === 'hard' && raidHardDef(raid) ? 'hard' : 'normal', tn = raidTuning(raid, d);
  const maxHp = Math.round(tn.hp * (RAID_HP_MULT[Math.max(1, Math.min(4, humans))] ?? 1));
  const w: RaidWorld = {
    raid, diff: d, tick: 0, rng: (seed ^ 0x52414944) >>> 0,
    boss: { x: RAID_ARENA.bossAt.x, z: RAID_ARENA.bossAt.z, f: 0, hp: maxHp, maxHp, state: RBoss.Intro, stateTick: 0, air: 0, phase: 1, enraged: false, enrageIn: tn.enrageAt, stagger: 0, staggerAcc: 0 },
    slots: [], teles: [], fires: [], teleSeq: 0,
    target: -1, tauntBy: -1, tauntTicks: 0, retarget: 0, cooldown: 0, attacks: 0, lastPattern: -1,
    pattern: -1, dashLeft: 0, dashAngle: 0,
    flight: 0, flightClock: 0, groundLeft: 0, breathMade: 0, landX: 0, landZ: 0,
    over: 0, out: [],
  };
  for (let i = 0; i < RAID_CAP; i++) {
    const s = emptySlot(), sp = specs[i], at = RAID_ARENA.spawns[i];
    s.x = at.x; s.z = at.z;
    if (sp && sp.kind === RaidSlotKind.Human) {
      s.kind = RaidSlotKind.Human; s.maxHp = s.hp = raidServerHp(sp.level);
    } else if (sp && sp.kind === RaidSlotKind.Merc && sp.merc !== undefined && MERCS[sp.merc]) {
      const d = MERCS[sp.merc], st = mercStats(d, sp.level);
      s.kind = RaidSlotKind.Merc; s.merc = sp.merc; s.maxHp = s.hp = st.maxHp; s.atk = st.attack;
      s.hitClock = 30 + i * 17; s.skillClock = d.role === 'tank' ? 120 : d.role === 'priest' ? HEAL.every : 0;
    }
    w.slots.push(s);
  }
  w.boss.f = Math.atan2(RAID_ARENA.cx - w.boss.x, -31 - w.boss.z);
  return w;
}

export const slotAlive = (s: RaidSlot): boolean => s.kind !== RaidSlotKind.None && !s.gone && s.down === 0 && s.hp > 0;
/** 사람이 판에서 빠졌다: 쓰러뜨리고 다시 일으키지 않는다(PR #263 Codex P2 — 나간 칸이 15초 뒤 유령으로 살아나 어그로·전멸 판정을 막았다) */
export function leaveSlot(w: RaidWorld, i: number): void {
  const s = w.slots[i];
  if (!s || s.kind !== RaidSlotKind.Human || s.gone) return;
  downSlot(w, i);
  s.gone = true; s.down = 0; s.hp = 0; s.act = RaidAct.Down;
}
/** 보스가 피해를 받는가(등장·비행·쓰러짐 무적) */
export function bossVulnerable(w: RaidWorld): boolean {
  const s = w.boss.state;
  return w.over === 0 && w.boss.hp > 0 && w.boss.air === 0 && w.flight === 0 && s !== RBoss.Intro && s !== RBoss.Dying && s !== RBoss.Dead;
}

// ── 판정 모양(game/combat.ts telegraphHit과 같은 식 — raidNet.test.ts가 같음을 본다) ──
export function teleHit(t: { shape: number; x: number; z: number; r: number; a: number; w: number }, tx: number, tz: number): boolean {
  const dx = tx - t.x, dz = tz - t.z;
  if (t.shape === TeleShape.Circle) return dx * dx + dz * dz <= t.r * t.r;
  if (t.shape === TeleShape.Cone) {
    const d = Math.hypot(dx, dz);
    if (d > t.r) return false;
    if (d <= 0 || t.w >= Math.PI * 2) return true;
    return (dx * Math.sin(t.a) + dz * Math.cos(t.a)) / d >= Math.cos(Math.min(Math.PI, t.w / 2));
  }
  const along = dx * Math.sin(t.a) + dz * Math.cos(t.a);
  const side = dx * Math.cos(t.a) - dz * Math.sin(t.a);
  return along >= 0 && along <= t.r && Math.abs(side) <= t.w / 2;
}

/** 무대 안(원, 반경 r 몸이 들어가는가) */
export function inArena(x: number, z: number, radius: number): boolean {
  return Math.hypot(x - RAID_ARENA.cx, z - RAID_ARENA.cz) <= RAID_ARENA.r - radius;
}

// ── 위협·피해 ─────────────────────────────────────────

function addThreat(w: RaidWorld, slot: number, amount: number): void {
  if (amount > 0 && w.slots[slot]) w.slots[slot].threat += amount;
}

/**
 * 보스 피해(사람 타격은 서버가 자른 뒤, 용병 타격은 시뮬 안에서). 실제로 줄어든 HP를 돌려준다.
 * 페이즈 2(50%)·경직(최대 HP 12% 누적)·쓰러짐을 여기서 정한다(1단계 bossDamaged와 같은 규칙)
 */
export function raidHitBoss(w: RaidWorld, slot: number, damage: number): number {
  const b = w.boss;
  if (!bossVulnerable(w) || !(damage > 0)) return 0;
  const dealt = Math.min(b.hp, Math.min(DAMAGE_CAP, Math.round(damage)));
  if (dealt <= 0) return 0;
  b.hp -= dealt;
  const s = w.slots[slot];
  if (s) { s.dmg += dealt; addThreat(w, slot, dealt * (s.kind === RaidSlotKind.Merc ? MERCS[s.merc].threat : 1)); }
  if (b.hp <= 0) {
    b.hp = 0; cancelPattern(w); w.flight = 0; b.air = 0; w.fires.length = 0;
    setBoss(w, RBoss.Dying); w.out.push({ e: 'boss', k: 'dying' });
    return dealt;
  }
  if (b.phase === 1 && b.hp <= b.maxHp * WYVERN.flight.phase) {
    b.phase = 2; w.flightClock = WYVERN.flight.firstDelay; w.groundLeft = 0;
    w.out.push({ e: 'pat', id: WyvernPattern.phase2 });
  }
  if (b.state === RBoss.Stagger) return dealt;
  b.staggerAcc += dealt;
  const th = b.maxHp * WYVERN.staggerRatio;
  b.stagger = Math.min(1, b.staggerAcc / th);
  if (b.staggerAcc >= th) {
    b.staggerAcc -= th; cancelPattern(w); setBoss(w, RBoss.Stagger); b.stagger = 1;
    w.out.push({ e: 'boss', k: 'stagger' });
  }
  return dealt;
}

/** 칸 피해: 용병은 받는 피해 배율, 사람은 서버 대략 체력. 0이 되면 쓰러짐(일으키기 시계) */
function hurtSlot(w: RaidWorld, i: number, damage: number): void {
  const s = w.slots[i];
  if (!slotAlive(s)) return;
  const mult = s.kind === RaidSlotKind.Merc ? MERCS[s.merc].damageTaken : 1;
  const d = Math.max(1, Math.round(damage * mult));
  s.hp = Math.max(0, s.hp - d);
  w.out.push({ e: 'hurt', s: i, d, hp: s.hp });
  if (s.hp === 0) downSlot(w, i);
}
/** 쓰러짐(서버 판정 또는 사람이 알린 쓰러짐) */
export function downSlot(w: RaidWorld, i: number): void {
  const s = w.slots[i];
  if (!s || s.kind === RaidSlotKind.None || s.down > 0) return;
  s.hp = 0; s.down = RAID_REVIVE_TICKS; s.act = RaidAct.Down; s.actTick = 0; s.evading = false; s.dodgeAt = -1;
  if (w.tauntBy === i) { w.tauntBy = -1; w.tauntTicks = 0; }
  w.out.push({ e: 'mev', s: i, k: MevKind.Down, a: RAID_REVIVE_TICKS, c: 0 });
}

// ── 한 틱 ─────────────────────────────────────────────

/** 시뮬 한 틱(60Hz). 사람 칸 위치는 호출자가 미리 옮겨 둔다(서버가 받은 pos). 끝나면 w.over */
export function raidStep(w: RaidWorld): void {
  if (w.over !== 0) return;
  w.tick++;
  if (w.tauntTicks > 0 && --w.tauntTicks === 0) w.tauntBy = -1;
  stepFires(w);
  const fight = w.boss.state !== RBoss.Intro && w.boss.state !== RBoss.Dying && w.boss.state !== RBoss.Dead;
  for (let i = 0; i < w.slots.length; i++) stepSlot(w, i, fight);
  stepBoss(w);
  // 전멸: 칸이 모두 쓰러짐(일어나기 전)
  if (w.over === 0 && w.boss.state !== RBoss.Dying && w.boss.state !== RBoss.Dead && !w.slots.some(slotAlive)) w.over = 2;
}

function stepFires(w: RaidWorld): void {
  const F = WYVERN.fire, dmg = tuneOf(w).damage * F.dmg * dmgMult(w);
  for (let k = w.fires.length - 1; k >= 0; k--) {
    const f = w.fires[k];
    if (--f.ttl <= 0) { w.fires.splice(k, 1); continue; }
    if (--f.clock > 0) continue;
    f.clock = F.every;
    for (let i = 0; i < w.slots.length; i++) {
      const s = w.slots[i];
      if (slotAlive(s) && Math.hypot(s.x - f.x, s.z - f.z) < f.r) hurtSlot(w, i, dmg);
    }
  }
}

function stepSlot(w: RaidWorld, i: number, fight: boolean): void {
  const s = w.slots[i];
  if (s.kind === RaidSlotKind.None) return;
  if (s.gone) return;
  if (s.actTick < 0xffff) s.actTick++;
  if (s.down > 0) {
    if (--s.down === 0) {
      s.hp = Math.max(1, Math.round(s.maxHp * MERC_REVIVE.hpRatio)); s.act = RaidAct.Idle; s.actTick = 0;
      w.out.push({ e: 'mev', s: i, k: MevKind.Revive, a: s.hp, c: 0 });
    }
    return;
  }
  if (s.kind !== RaidSlotKind.Merc) return;
  const d = MERCS[s.merc], b = w.boss;
  if (s.hitClock > 0) s.hitClock--;
  if (s.skillClock > 0) s.skillClock--;
  // 1) 위험 → 반응 지연 뒤 피하기
  const danger = dangerAt(w, s.x, s.z, d.radius, i);
  if (danger && s.dodgeAt < 0 && !s.evading) s.dodgeAt = w.tick + rngInt(w, d.react[0], d.react[1] + 1);
  if (!danger && !s.evading) s.dodgeAt = -1;
  if (s.dodgeAt >= 0 && w.tick >= s.dodgeAt && !s.evading) {
    s.dodgeAt = -1;
    if (safeSpot(w, s, d.radius, i)) { s.evading = true; w.out.push({ e: 'mev', s: i, k: MevKind.Dodge, a: 0, c: 0 }); }
  }
  if (s.evading) {
    const arrived = moveToward(s, s.evX, s.evZ, d.speed * 1.4, d.radius, 0.3);
    s.act = RaidAct.Move;
    if (arrived || (!dangerAt(w, s.x, s.z, d.radius, i) && s.actTick > 8)) s.evading = false;
    return;
  }
  // 2) 역할 자리
  if (!fight) { follow(w, s, i, d.radius, d.speed); return; }
  const dist = Math.hypot(s.x - b.x, s.z - b.z) - WYVERN.radius;
  const want = rolePoint(w, i, s.merc, d.keepMin, d.keepMax);
  const far = dist > d.keepMax || dist < d.keepMin;
  if (far) { moveToward(s, want.x, want.z, d.speed, d.radius, 0.4); s.act = RaidAct.Move; }
  else if (s.act === RaidAct.Move) s.act = RaidAct.Idle;
  s.f = Math.atan2(b.x - s.x, b.z - s.z);
  // 3) 도발·치유
  if (d.role === 'tank' && s.skillClock === 0 && bossVulnerable(w)) {
    s.skillClock = TAUNT.every; w.tauntTicks = TAUNT.ticks; w.tauntBy = i; w.retarget = 0;
    s.act = RaidAct.Cast; s.actTick = 0;
    w.out.push({ e: 'mev', s: i, k: MevKind.Taunt, a: TAUNT.ticks, c: 0 });
  }
  if (d.role === 'priest' && s.skillClock === 0) heal(w, i);
  // 4) 공격
  if (s.hitClock === 0 && dist <= d.reach && bossVulnerable(w)) {
    s.hitClock = d.hitEvery; s.act = RaidAct.Attack; s.actTick = 0;
    const dealt = raidHitBoss(w, i, Math.max(1, Math.round(s.atk * d.hitMult * rngRange(w, 0.9, 1.1))));
    w.out.push({ e: 'mev', s: i, k: MevKind.Attack, a: dealt, c: 0 });
  }
  if ((s.act === RaidAct.Attack || s.act === RaidAct.Cast) && s.actTick > 24) s.act = far ? RaidAct.Move : RaidAct.Idle;
}

/** 전투 전: 가장 가까운 사람 뒤를 따라간다 */
function follow(w: RaidWorld, s: RaidSlot, i: number, radius: number, speed: number): void {
  let lead: RaidSlot | null = null, best = Infinity;
  for (const o of w.slots) if (o.kind === RaidSlotKind.Human && slotAlive(o)) { const d = Math.hypot(o.x - s.x, o.z - s.z); if (d < best) { best = d; lead = o; } }
  if (!lead) { s.act = RaidAct.Idle; return; }
  const a = lead.f + Math.PI + (i - 2) * 0.6, r = 2.4;
  const tx = lead.x + Math.sin(a) * r, tz = lead.z + Math.cos(a) * r;
  if (Math.hypot(s.x - tx, s.z - tz) > 1.2) { moveToward(s, tx, tz, speed, radius, 0.6); s.act = RaidAct.Move; }
  else { s.act = RaidAct.Idle; s.f = lead.f; }
}

const ROLE_TRIES = [0, deg(30), deg(-30), deg(60), deg(-60), deg(90), deg(-90)] as const;
const _pt = { x: 0, z: 0 };
function rolePoint(w: RaidWorld, i: number, merc: number, keepMin: number, keepMax: number): { x: number; z: number } {
  const b = w.boss, r = WYVERN.radius + (keepMin + keepMax) / 2;
  // 방패 = 정면, 궁수·사제 = 좌·우 뒤쪽 비스듬히(같은 역할이 둘이면 칸 번호로 갈라 선다)
  const spread = merc === 0 ? 0 : merc === 1 ? deg(120) : deg(-120);
  const jitter = (i % 2 ? 1 : -1) * deg(8);
  for (let k = 0; k < ROLE_TRIES.length; k++) {
    const a = b.f + spread + jitter + ROLE_TRIES[k];
    let x = b.x + Math.sin(a) * r, z = b.z + Math.cos(a) * r;
    const dx = x - RAID_ARENA.cx, dz = z - RAID_ARENA.cz, d = Math.hypot(dx, dz), lim = RAID_ARENA.r - RAID_ARENA.pad - 1;
    if (d > lim) { x = RAID_ARENA.cx + dx / d * lim; z = RAID_ARENA.cz + dz / d * lim; }
    _pt.x = x; _pt.z = z;
    if (!dangerAt(w, x, z, 0.8)) break;
  }
  return _pt;
}

function moveToward(s: RaidSlot, tx: number, tz: number, speed: number, radius: number, stopAt: number): boolean {
  const dx = tx - s.x, dz = tz - s.z, dist = Math.hypot(dx, dz);
  if (dist <= stopAt) return true;
  const step = Math.min(dist, speed * FIXED_DT), sx = dx / dist * step, sz = dz / dist * step;
  if (inArena(s.x + sx, s.z, radius)) s.x += sx;
  if (inArena(s.x, s.z + sz, radius)) s.z += sz;
  s.f = Math.atan2(dx, dz);
  return false;
}

function dangerAt(w: RaidWorld, x: number, z: number, radius: number, self = -1): boolean {
  for (const t of w.teles) {
    if (self >= 0 && t.o === self) continue; // 내 낙인은 따라온다 — 피하지 않고, 다른 칸이 내 원을 피한다
    if (teleHit(t, x, z) || teleHit(t, x + radius, z) || teleHit(t, x - radius, z) || teleHit(t, x, z + radius) || teleHit(t, x, z - radius)) return true;
  }
  for (const f of w.fires) if (Math.hypot(f.x - x, f.z - z) < f.r + radius) return true;
  return false;
}

const SAFE_RINGS = [3.4, 4.6, 6.2] as const;
function safeSpot(w: RaidWorld, s: RaidSlot, radius: number, self = -1): boolean {
  const off = rngNext(w) * Math.PI * 2, b = w.boss;
  for (const ring of SAFE_RINGS) {
    let found = false, bestD = Infinity, bx = 0, bz = 0;
    for (let k = 0; k < 16; k++) {
      const a = off + k * (Math.PI / 8), x = s.x + Math.sin(a) * ring, z = s.z + Math.cos(a) * ring;
      if (!inArena(x, z, radius + RAID_ARENA.pad)) continue;
      if (dangerAt(w, x, z, radius + 0.4, self)) continue;
      const db = Math.hypot(x - b.x, z - b.z);
      if (db < WYVERN.radius + radius + 0.3) continue;
      const score = Math.abs(db - 6);
      if (score < bestD) { bestD = score; found = true; bx = x; bz = z; }
    }
    if (found) { s.evX = bx; s.evZ = bz; return true; }
  }
  return false;
}

/** 사제 치유: HP 비율이 가장 낮은 아군(사람은 서버 대략 체력 — 사람 실제 체력 회복은 클라가 mev Heal을 받아 적용) */
function heal(w: RaidWorld, i: number): void {
  const s = w.slots[i];
  let best = -1, ratio = 0.92;
  for (let k = 0; k < w.slots.length; k++) {
    const o = w.slots[k];
    if (!slotAlive(o)) continue;
    const r = o.hp / Math.max(1, o.maxHp);
    if (r < ratio && Math.hypot(o.x - s.x, o.z - s.z) <= HEAL.range) { best = k; ratio = r; }
  }
  if (best < 0) { s.skillClock = 30; return; }
  s.skillClock = HEAL.every; s.act = RaidAct.Cast; s.actTick = 0;
  const o = w.slots[best], amount = Math.min(o.maxHp - o.hp, Math.round(o.maxHp * HEAL.ratio));
  o.hp += amount;
  addThreat(w, i, amount * HEAL.threat);
  w.out.push({ e: 'mev', s: i, k: MevKind.Heal, a: best, c: amount });
}

// ── 비룡 두뇌 ─────────────────────────────────────────

const dmgMult = (w: RaidWorld): number => (w.boss.enraged ? WYVERN.enrage.dmgMult : 1);
/** 판 수치(난이도). 레이드·난이도마다 한 객체를 캐시한다(C13 리뷰 P2-6 — 고정 스텝에서 문자열·객체를 만들지 않는다) */
const TUNE_CACHE = new Map<RaidId, { normal?: RaidTuning; hard?: RaidTuning }>();
export function tuneOf(w: { raid: RaidId; diff?: RaidDiff }): RaidTuning {
  let e = TUNE_CACHE.get(w.raid);
  if (!e) { e = {}; TUNE_CACHE.set(w.raid, e); }
  const d = w.diff === 'hard' ? 'hard' : 'normal';
  return e[d] ??= raidTuning(w.raid, d);
}
function setBoss(w: RaidWorld, state: number): void { w.boss.state = state; w.boss.stateTick = 0; }
function cancelPattern(w: RaidWorld): void {
  w.teles.length = 0; w.dashLeft = 0; w.pattern = -1;
}
function tele(w: RaidWorld, pattern: number, shape: TeleShapeId, x: number, z: number, r: number, a: number, width: number, total: number, mult: number, heavy: boolean, owner = -1): void {
  const t: RaidTele = { n: ++w.teleSeq, pattern, shape, x, z, r, a, w: width, born: w.tick, due: w.tick + total, mult, heavy, mask: 0 };
  if (owner >= 0) t.o = owner;
  w.teles.push(t);
  w.out.push({ e: 'tel', t });
}
function announce(w: RaidWorld, id: number): void { w.out.push({ e: 'pat', id }); }

function stepBoss(w: RaidWorld): void {
  const b = w.boss, W = WYVERN;
  if (b.stateTick < 0xffff) b.stateTick++;
  if (w.cooldown > 0) w.cooldown--;
  if (b.state === RBoss.Intro) { if (b.stateTick >= W.intro) { setBoss(w, RBoss.Chase); w.cooldown = 30; } return; }
  if (b.state === RBoss.Dying) { if (b.stateTick >= W.dying) { setBoss(w, RBoss.Dead); w.over = 1; w.out.push({ e: 'boss', k: 'dead' }); } return; }
  if (b.state === RBoss.Dead) return;
  if (!b.enraged && b.enrageIn > 0 && --b.enrageIn === 0) { b.enraged = true; announce(w, WyvernPattern.enrage); }
  if (b.phase === 2 && w.flight === 0 && w.flightClock > 0) w.flightClock--;
  if (b.state === RBoss.Stagger) { if (b.stateTick >= W.staggerTicks) setBoss(w, RBoss.Chase); return; }
  // 고정 스텝 안에서 새 배열을 만들지 않는다(AGENTS.md — 모듈 작업 버퍼 재사용, 판정은 동기라 DO끼리 섞이지 않는다)
  const alive = ALIVE, threat = THREAT;
  for (let i = 0; i < RAID_CAP; i++) { const sl = w.slots[i]; alive[i] = !!sl && slotAlive(sl); threat[i] = sl ? sl.threat : 0; }
  if (--w.retarget <= 0 || !alive[Math.max(0, w.target)]) {
    w.retarget = W.retargetEvery;
    w.target = pickTarget(w.target, alive, threat, w.tauntBy, W.retargetMargin);
  }
  if (w.flight > 0) { stepFlight(w, alive); return; }
  if (w.teles.length > 0) { stepPattern(w); return; }
  if (b.state === RBoss.Slam || b.state === RBoss.Sweep) {
    if (w.dashLeft > 0) { stepDash(w); return; }
    if (b.stateTick >= 21) setBoss(w, RBoss.Chase);
    return;
  }
  const t = w.target >= 0 ? w.slots[w.target] : null;
  if (!t) return;
  const dx = t.x - b.x, dz = t.z - b.z, dist = Math.hypot(dx, dz);
  if (dist > 1e-6) b.f = turn(b.f, Math.atan2(dx, dz), 0.09);
  if (w.cooldown === 0 && begin(w, dist, alive)) return;
  if (b.state !== RBoss.Chase) setBoss(w, RBoss.Chase);
  if (dist > WYVERN.radius + W.bite.reach * 0.8) {
    const step = W.speed * FIXED_DT, sx = dx / dist * step, sz = dz / dist * step;
    if (inArena(b.x + sx, b.z, WYVERN.radius)) b.x += sx;
    if (inArena(b.x, b.z + sz, WYVERN.radius)) b.z += sz;
  }
}

/** 고정 스텝 작업 버퍼(칸 생존·위협, 사람 판정용 줄인 경고) */
const ALIVE: boolean[] = new Array<boolean>(RAID_CAP).fill(false);
const THREAT = new Float64Array(RAID_CAP);
const SHRUNK = { shape: 0, x: 0, z: 0, r: 0, a: 0, w: 0 };
const P1_SEQ = [WyvernPattern.bite, WyvernPattern.pools, WyvernPattern.bite, WyvernPattern.charge] as const;
const P2_SEQ = [WyvernPattern.pools, WyvernPattern.bite, WyvernPattern.charge, WyvernPattern.bite] as const;

function begin(w: RaidWorld, dist: number, alive: boolean[]): boolean {
  const b = w.boss, W = WYVERN;
  if (b.phase === 2 && w.flightClock === 0 && w.groundLeft <= 0) return takeoff(w);
  let behind = 0;
  for (let k = 0; k < w.slots.length; k++) {
    if (!alive[k]) continue;
    const p = w.slots[k], d = Math.hypot(p.x - b.x, p.z - b.z);
    if (d > W.tail.range + WYVERN.radius * 0.3) continue;
    if (Math.abs(angleDiff(Math.atan2(p.x - b.x, p.z - b.z), b.f)) > deg(110)) behind++;
  }
  let kind: number;
  const B = tuneOf(w).brand;
  // 하드 전용 잿불 낙인(C13, 솔로 game/raid.ts startBrand와 같은 규칙): 지상 공격 every번마다
  if (B && w.attacks % B.every === B.every - 1 && w.lastPattern !== WyvernPattern.brand) kind = WyvernPattern.brand;
  else if (behind >= 2 && w.lastPattern !== WyvernPattern.tail) kind = WyvernPattern.tail;
  else {
    const seq = b.phase === 2 ? P2_SEQ : P1_SEQ;
    kind = seq[w.attacks % seq.length];
    if (kind === WyvernPattern.bite && dist > WYVERN.radius + W.bite.reach) {
      if (dist > WYVERN.radius + 9) kind = WyvernPattern.charge;
      else return false;
    }
  }
  cancelPattern(w);
  if (kind === WyvernPattern.bite) { tele(w, kind, TeleShape.Cone, b.x, b.z, W.bite.range + WYVERN.radius * 0.5, b.f, deg(W.bite.arcDeg), W.bite.windup, W.bite.dmg, true); setBoss(w, RBoss.SlamWindup); }
  else if (kind === WyvernPattern.tail) { tele(w, kind, TeleShape.Cone, b.x, b.z, W.tail.range + WYVERN.radius * 0.5, b.f + Math.PI, deg(W.tail.arcDeg), W.tail.windup, W.tail.dmg, false); setBoss(w, RBoss.SweepWindup); }
  else if (kind === WyvernPattern.brand && B) {
    let mask = 0, k = 0;
    const first = w.target >= 0 && alive[w.target] ? w.target : randomAlive(w, alive);
    for (let tries = 0; k < B.marks && tries < 8; tries++) {
      const who = k === 0 ? first : randomAlive(w, alive);
      if (who < 0 || (mask & (1 << who))) continue;
      tele(w, kind, TeleShape.Circle, w.slots[who].x, w.slots[who].z, B.radius, 0, 0, B.windup, B.bearerDmg, false, who);
      mask |= 1 << who; k++;
    }
    if (k === 0) return false;
    setBoss(w, RBoss.Cast);
  } else if (kind === WyvernPattern.pools) {
    for (let p = 0; p < w.slots.length; p++) if (alive[p]) tele(w, kind, TeleShape.Circle, w.slots[p].x, w.slots[p].z, W.pools.radius, 0, 0, W.pools.windup, W.pools.dmg, false);
    const extra = (b.phase === 2 ? W.pools.extraP2 : 0) + tuneOf(w).poolsExtraP1;
    for (let e = 0; e < extra; e++) {
      const a = rngNext(w) * Math.PI * 2, r = rngRange(w, 3, Math.min(W.pools.scatter, RAID_ARENA.r - 3));
      tele(w, kind, TeleShape.Circle, RAID_ARENA.cx + Math.sin(a) * r, RAID_ARENA.cz + Math.cos(a) * r, W.pools.radius, 0, 0, W.pools.windup, W.pools.dmg, false);
    }
    if (w.teles.length === 0) return false;
    setBoss(w, RBoss.Cast);
  } else {
    const t = w.target >= 0 ? w.slots[w.target] : w.slots[0];
    const a = Math.atan2(t.x - b.x, t.z - b.z);
    tele(w, kind, TeleShape.Line, b.x, b.z, W.charge.length, a, W.charge.width, W.charge.windup, W.charge.dmg, true);
    b.f = a; w.dashAngle = a; setBoss(w, RBoss.SweepWindup);
  }
  w.pattern = kind; announce(w, kind);
  w.attacks++; w.lastPattern = kind;
  if (b.phase === 2) w.groundLeft--;
  return true;
}

/** 경고 판정: 모양 안의 칸(아직 안 맞은)에게 피해. 사람은 서버 위치로 대략 체력만 깎는다(실제 피격은 클라가 같은 경고로 판정) */
function strike(w: RaidWorld, t: RaidTele): void {
  const tn = tuneOf(w), owner = t.o ?? -1, B = tn.brand;
  const dmg = tn.damage * t.mult * dmgMult(w);
  for (let i = 0; i < w.slots.length; i++) {
    const s = w.slots[i], bit = 1 << i;
    if (t.mask & bit || !slotAlive(s)) continue;
    // 잿불 낙인: 주인은 늘 맞고(bearerDmg), 원 안의 다른 칸은 crowdDmg
    if (owner >= 0 && B) {
      if (i !== owner && !teleHit(t, s.x, s.z)) continue;
      t.mask |= bit;
      hurtSlot(w, i, tn.damage * (i === owner ? B.bearerDmg : B.crowdDmg) * dmgMult(w));
      continue;
    }
    // 사람: 서버 위치는 지연된 값이라 0.5m 안쪽만 센다(경계에서 피한 사람을 서버가 쓰러뜨리지 않게)
    let hit: boolean;
    if (s.kind === RaidSlotKind.Human) {
      const sh = SHRUNK; sh.shape = t.shape; sh.x = t.x; sh.z = t.z; sh.a = t.a;
      sh.r = Math.max(0, t.r - (t.shape === TeleShape.Line ? 0 : 0.5)); sh.w = t.shape === TeleShape.Line ? Math.max(0, t.w - 1) : t.w;
      hit = teleHit(sh, s.x, s.z);
    } else hit = teleHit(t, s.x, s.z);
    if (!hit) continue;
    t.mask |= bit;
    hurtSlot(w, i, dmg);
  }
  w.out.push({ e: 'boom', n: t.n });
}

function stepPattern(w: RaidWorld): void {
  const b = w.boss;
  for (let k = w.teles.length - 1; k >= 0; k--) {
    const t = w.teles[k];
    // 잿불 낙인은 주인을 따라다닌다(쓰러지면 그 자리에 남는다). 클라도 주인 위치로 그린다(o)
    if ((t.o ?? -1) >= 0) { const s = w.slots[t.o!]; if (s && slotAlive(s)) { t.x = s.x; t.z = s.z; } }
    if (w.tick < t.due) continue;
    strike(w, t);
    if (w.pattern === WyvernPattern.pools || (t.o ?? -1) >= 0) {
      const F = WYVERN.fire;
      if (w.fires.length >= F.max) w.fires.shift();
      w.fires.push({ x: t.x, z: t.z, r: F.radius, ttl: F.ticks, clock: F.every });
      w.out.push({ e: 'fire', x: t.x, z: t.z, r: F.radius, ttl: F.ticks });
    }
    w.teles.splice(k, 1);
  }
  if (w.teles.length > 0) return;
  const id = w.pattern;
  setBoss(w, id === WyvernPattern.tail || id === WyvernPattern.pools || id === WyvernPattern.brand ? RBoss.Sweep : RBoss.Slam);
  if (id === WyvernPattern.charge) w.dashLeft = WYVERN.charge.length - WYVERN.radius * 0.5;
  w.cooldown = Math.round(WYVERN.cooldown * tuneOf(w).cooldownMult * (b.enraged ? WYVERN.enrage.cooldownMult : 1));
  w.pattern = -1;
}

function stepDash(w: RaidWorld): void {
  const b = w.boss, C = WYVERN.charge;
  const step = Math.min(w.dashLeft, C.length / C.dashTicks);
  const sx = Math.sin(w.dashAngle) * step, sz = Math.cos(w.dashAngle) * step;
  let moved = false;
  if (inArena(b.x + sx, b.z, WYVERN.radius)) { b.x += sx; moved = true; }
  if (inArena(b.x, b.z + sz, WYVERN.radius)) { b.z += sz; moved = true; }
  w.dashLeft = moved ? w.dashLeft - step : 0;
  if (w.dashLeft <= 1e-3 || b.stateTick >= C.dashTicks + 21) { w.dashLeft = 0; setBoss(w, RBoss.Chase); }
}

function takeoff(w: RaidWorld): boolean {
  const F = WYVERN.flight;
  cancelPattern(w);
  w.flight = 1; w.breathMade = 0; w.boss.air = 0.001;
  setBoss(w, RBoss.Cast); announce(w, WyvernPattern.takeoff);
  w.flightClock = F.every; w.groundLeft = F.groundAttacks;
  return true;
}

function stepFlight(w: RaidWorld, alive: boolean[]): void {
  const b = w.boss, F = WYVERN.flight;
  if (w.flight === 1) {
    b.air = Math.min(1, b.stateTick / F.takeoff + 0.001);
    if (b.stateTick >= F.takeoff) { w.flight = 2; w.breathMade = 0; b.stateTick = 0; }
    return;
  }
  if (w.flight === 2) {
    if (w.breathMade < F.breaths && b.stateTick >= w.breathMade * F.breathGap) {
      const pick = randomAlive(w, alive), p = w.slots[pick < 0 ? 0 : pick];
      const a = rngNext(w) * Math.PI, half = F.breathLength / 2;
      tele(w, WyvernPattern.breath, TeleShape.Line, p.x - Math.sin(a) * half, p.z - Math.cos(a) * half, F.breathLength, a, F.breathWidth, F.breathWindup, F.breathDmg, false);
      announce(w, WyvernPattern.breath);
      b.f = a; w.breathMade++;
    }
    for (let k = w.teles.length - 1; k >= 0; k--) {
      const t = w.teles[k];
      if (w.tick < t.due) continue;
      strike(w, t);
      w.teles.splice(k, 1);
      toward(w, t.x + Math.sin(t.a) * t.r * 0.5, t.z + Math.cos(t.a) * t.r * 0.5, RAID_ARENA.r - 4);
    }
    if (w.breathMade < F.breaths || w.teles.length > 0) return;
    const t = w.target >= 0 ? w.slots[w.target] : w.slots[0];
    w.landX = t.x; w.landZ = t.z;
    if (!inArena(w.landX, w.landZ, WYVERN.radius)) { w.landX = RAID_ARENA.cx; w.landZ = RAID_ARENA.cz; }
    w.flight = 3; b.stateTick = 0;
    tele(w, WyvernPattern.land, TeleShape.Circle, w.landX, w.landZ, F.landRadius, 0, 0, F.landWindup, F.landDmg, true);
    announce(w, WyvernPattern.land);
    return;
  }
  const k = Math.min(1, b.stateTick / F.landWindup);
  b.air = Math.max(0.001, 1 - k);
  b.x += (w.landX - b.x) * 0.08; b.z += (w.landZ - b.z) * 0.08;
  if (b.stateTick < F.landWindup) return;
  b.x = w.landX; b.z = w.landZ; b.air = 0;
  const t = w.teles[0];
  if (t) strike(w, t);
  w.teles.length = 0; w.flight = 0;
  setBoss(w, RBoss.Slam); w.cooldown = Math.round(WYVERN.cooldown * tuneOf(w).cooldownMult); w.pattern = -1;
}

function toward(w: RaidWorld, x: number, z: number, lim: number): void {
  const b = w.boss, dx = x - RAID_ARENA.cx, dz = z - RAID_ARENA.cz, d = Math.hypot(dx, dz);
  if (d > lim && d > 0) { x = RAID_ARENA.cx + dx / d * lim; z = RAID_ARENA.cz + dz / d * lim; }
  b.x = x; b.z = z;
}
function randomAlive(w: RaidWorld, alive: boolean[]): number {
  let n = 0;
  for (let k = 0; k < alive.length; k++) if (alive[k]) n++;
  if (n === 0) return -1;
  let r = rngInt(w, 0, n);
  for (let k = 0; k < alive.length; k++) if (alive[k] && r-- === 0) return k;
  return 0;
}
function angleDiff(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}
function turn(from: number, to: number, max: number): number { return from + Math.max(-max, Math.min(max, angleDiff(to, from))); }

// ── 프로토콜 ─────────────────────────────────────────

export type RaidPhase = 'lobby' | 'count' | 'run' | 'end';
export interface RaidMemberView { slot: number; name: string; hero: HeroId; lv: number; ready: boolean; online: boolean; leader: boolean }
export interface RaidRoomView {
  code: string; raid: RaidId; phase: RaidPhase; members: RaidMemberView[];
  /** 판에서 용병이 맡을 칸(MERCS 번호, 사람 칸은 -1) */
  mercs: number[];
  runId: string; startAt: number;
  /** 자동 충원 출발 시각(0 = 아직 사람 없음) */
  fillAt: number;
  /** 이번 주 남은 보상(내 계정, -1 = 모름) */
  weekLeft: number;
  /** 난이도(C13, 옛 서버는 없음 = 노말) */
  diff?: RaidDiff;
}

/** 클라 → 서버 */
export type RaidClientMsg =
  | { t: 'hello'; v: number; name: string; hero: HeroId; lv: number }
  | { t: 'ready'; on: boolean }
  | { t: 'start' }
  | { t: 'leave' }
  /** 내 위치(10~15Hz): x·z·방향·동작 */
  | { t: 'pos'; x: number; z: number; f: number; a: number }
  /** 보스 타격(피해량 — 서버가 자른다) */
  | { t: 'hit'; d: number }
  /** 내 영웅이 쓰러졌다(클라 판정). 일으키기는 서버가 한다 */
  | { t: 'down' }
  /** 레이드 장면을 다 읽었다(판 시작 장벽 — 모두 보내거나 RAID_LOAD_MAX_MS가 지나야 보스가 움직인다) */
  | { t: 'loaded' };

/** 스냅 칸: [칸, 종류, x, z, 방향, 동작, 체력, 최대 체력, 쓰러짐 남은 틱] */
export type RaidSnapSlot = [number, number, number, number, number, number, number, number, number];
/** 스냅 보스: [x, z, 방향, 체력, 최대 체력, 상태, 비행 ×100, 페이즈, 광폭 0/1, 경직 ×100, 표적 칸, 도발 칸, 광폭까지 틱] */
export type RaidSnapBoss = [number, number, number, number, number, number, number, number, number, number, number, number, number];
export interface RaidSync {
  tick: number;
  boss: RaidSnapBoss;
  slots: RaidSnapSlot[];
  teles: RaidTele[];
  fires: RaidFire[];
}

/** 서버 → 클라 */
export type RaidServerMsg =
  | { t: 'room'; room: RaidRoomView; you: number }
  | { t: 'err'; code: string }
  /** 판 시작(카운트다운 끝 시각 at) — 판 시뮬 틱 0이 at */
  | { t: 'go'; runId: string; at: number; you: number; mercs: number[]; bossHp: number; /** 난이도(C13) */ diff?: RaidDiff }
  /** 전체 상태(시작·재접속 복원) */
  | { t: 'sync'; s: RaidSync }
  /** 10Hz 스냅: k = 시뮬 틱 */
  | { t: 's'; k: number; b: RaidSnapBoss; p: RaidSnapSlot[] }
  /** 사건 묶음(시뮬 틱 k까지) */
  | { t: 'ev'; k: number; ev: RaidEvent[] }
  | { t: 'correct'; x: number; z: number }
  | { t: 'end'; ok: boolean; reason: string; runId: string; ms: number; dmg: number[]; eligible: boolean; reward: RaidRewardView | null; weekLeft: number; /** 하드 첫 클리어(C13): 이 판에서 첫 클리어 행이 들어갔으면 보상 요약 */ first?: { gold: number; legend: string; stones?: number } | null };

/** 끝 화면 보상(우편함으로 간다). n = 이 캐릭터의 이번 주 몇 번째(솔로 영수증 포함), tier = 강화석 등급, protectAlt = 보호 주문서 미해금이면 대신 받을 강화석 */
export interface RaidRewardView { n: number; gold: number; set: string; legend: string; stones: number; protect: number; items: number; tier?: number; protectAlt?: number }

export const q2 = (v: number): number => Math.round(v * 100) / 100;

export function snapBoss(w: RaidWorld): RaidSnapBoss {
  const b = w.boss;
  return [q2(b.x), q2(b.z), q2(b.f), b.hp, b.maxHp, b.state, Math.round(b.air * 100), b.phase, b.enraged ? 1 : 0, Math.round(b.stagger * 100), w.target, w.tauntBy, b.enrageIn];
}
export function snapSlots(w: RaidWorld): RaidSnapSlot[] {
  const out: RaidSnapSlot[] = [];
  w.slots.forEach((s, i) => { if (s.kind !== RaidSlotKind.None) out.push([i, s.kind === RaidSlotKind.Merc ? 10 + s.merc : 1, q2(s.x), q2(s.z), q2(s.f), s.act, s.hp, s.maxHp, s.down]); });
  return out;
}
export function raidSync(w: RaidWorld): RaidSync {
  return { tick: w.tick, boss: snapBoss(w), slots: snapSlots(w), teles: w.teles.map((t) => ({ ...t })), fires: w.fires.map((f) => ({ ...f })) };
}

/** 이름 정리(파티와 같은 규칙) */
export function cleanRaidName(raw: unknown): string {
  const s = typeof raw === 'string' ? raw.replace(/[\u0000-\u001f\u007f<>]/g, '').trim() : '';
  return [...s].slice(0, 12).join('') || '모험가';
}

// ── 보상(서버 RNG) ───────────────────────────────────

/**
 * 서버 보상 한 사람 몫. stones·tier = 강화석(솔로 raidReward와 같은 개수·등급), protect = 이 레이드의 주 첫 클리어 보호 주문서 수
 * (서버 장부 n = 1일 때만 우편으로 간다), protectAlt = 보호 주문서를 아직 못 쓰는 영웅(지역6 전)이 대신 받을 강화석 수(받을 때 클라가 바꾼다 — C1a)
 */
export interface RaidNetReward { gold: number; set: string; legend: string; items: GearItem[]; stones: number; tier: StoneTier; protect: number; protectAlt: number }
/** 지역6 전에는 보호 주문서 대신 줄 강화석(솔로 raidReward PROTECT_AS_STONES와 같은 값 — raid.test가 맞춰 본다) */
export const RAID_PROTECT_AS_STONES = 10;
/**
 * 한 사람의 레이드 클리어 보상(RAID-PRD 6절 표 — 세트 부위 1·전설 5%·골드). 씨앗 = 판 id + 계정 + 서버 비밀 소금(공개 runId로 미리 굴릴 수 없다).
 * uid = srv:<판 id>:<계정 앞 8>:<칸> — 서버 발급 개체(거래소 인증 장비). 강화석·보호 주문서는 우편 종류 stones·protect로 간다(C1a, docs/RAID-NET.md 4절).
 * 강화석·주문서 몫은 난수를 쓰지 않는다(장비 굴림 순서가 예전과 같다)
 */
export function raidNetReward(raid: RaidId, runId: string, who: string, hero: HeroId, salt: string, diff: RaidDiff = 'normal'): RaidNetReward {
  const def = RAIDS.find((r) => r.id === raid)!, loot = def.loot;
  // 하드(C13): 솔로 raidReward와 같은 배율(골드·전설 확률·세트 아이템 레벨). 노말은 예전과 같은 굴림
  const hard = diff === 'hard' ? raidHardDef(raid) : null;
  const r = seedRng(hashSeed('raid:' + runId + ':' + who + ':' + salt));
  const rand = () => nextRand(r);
  const tag = who.replace(/[^A-Za-z0-9]/g, '').slice(0, 8);
  const setId = loot.sets[Math.min(loot.sets.length - 1, Math.floor(rand() * loot.sets.length))];
  const pieces = EQUIPS.filter((e) => e.setId === setId);
  const piece = pieces[Math.min(pieces.length - 1, Math.floor(rand() * pieces.length))];
  const items: GearItem[] = [rolledItem(rand, piece, 'set', hard ? Math.max(piece.level, hard.loot.ilvl) : piece.level, `srv:${runId}:${tag}:set`)];
  let legend = '';
  if (rand() < (hard ? hard.loot.legendChance : loot.legendChance)) {
    const it = rollGear(rand, { region: piece.region, diff: hard ? 'hard' : 'normal', table: 'drop_final', hero }, 'boss', `srv:${runId}:${tag}:legend`, 0, { min: 'legend', max: 'legend' });
    items.push(it); legend = it.def;
  }
  return {
    gold: Math.round(loot.gold * (hard ? hard.loot.goldMult : 1)), set: piece.id, legend, items,
    stones: Math.round(loot.stones * (hard ? hard.loot.stonesMult : 1)), tier: stoneTierForIlvl(hard ? hard.recLevel : def.recLevel),
    protect: loot.protectFirstClear, protectAlt: loot.protectFirstClear > 0 ? RAID_PROTECT_AS_STONES : 0,
  };
}

/**
 * 하드 첫 클리어 보상(서버, C13): 확정 전설 1 + 첫 클리어 골드. 사람마다 한 번 — D1 raid_clears (주체, '<id>.hard:first', 0, 1) 행이 막는다.
 * 씨앗은 판 id + 계정 + 소금(주 보상과 다른 꼬리표), uid = srv:<판>:<계정 앞 8>:first
 */
export function raidNetFirstReward(raid: RaidId, runId: string, who: string, hero: HeroId, salt: string): RaidNetReward | null {
  const hard = raidHardDef(raid);
  if (!hard) return null;
  const r = seedRng(hashSeed('raidfirst:' + runId + ':' + who + ':' + salt));
  const rand = () => nextRand(r);
  const tag = who.replace(/[^A-Za-z0-9]/g, '').slice(0, 8);
  const def = RAIDS.find((x) => x.id === raid)!;
  const piece = EQUIPS.find((e) => e.setId === def.loot.sets[0])!;
  const it = rollGear(rand, { region: piece.region, diff: 'hard', table: 'drop_final', hero }, 'boss', `srv:${runId}:${tag}:first`, 0, { min: 'legend', max: 'legend' });
  return { gold: hard.first.gold, set: '', legend: it.def, items: [it], stones: hard.first.stones, tier: stoneTierForIlvl(hard.recLevel), protect: 0, protectAlt: 0 };
}
/** 하드 첫 클리어 장부 열쇠(raid_clears.raid) */
export function raidFirstKey(raid: RaidId): string { return raid + '.hard:first'; }
